import { getRequest } from "../../api/http";
import userServiceApi from "../../api/userServiceApi";
import { filterTabsBySubscriptionPlan } from "./planTabUtils";
import { getTabDisplayName } from "../constants";
import { getCanonicalTabName, getPlanTabDisplayName } from "./constants";

const planScopedTabsByMicrositeId = new Map();
const allTabsByMicrositeId = new Map();

function normalizeTabLabel(label = "") {
  return String(label).trim().toLowerCase().replace(/\s+/g, " ");
}

const formatMissingTabLabel = (name) => {
  const formatted = name
    ?.toLowerCase()
    ?.replace(/\b\w/g, (char) => char?.toUpperCase());
  return getTabDisplayName(formatted);
};

const collectMatchKeys = (name) => {
  const keys = new Set();
  if (!name) return keys;

  const variants = [
    name,
    formatMissingTabLabel(name),
    getTabDisplayName(name),
    getPlanTabDisplayName(name),
  ].filter(Boolean);

  variants.forEach((variant) => {
    keys.add(normalizeTabLabel(variant));
    keys.add(normalizeTabLabel(getCanonicalTabName(variant)));
  });

  return keys;
};

/** Never auto-disable these tabs when reported as missing by AI. */
const MANDATORY_MISSING_TAB_LABELS = new Set(
  [
    "Map Advert",
    "App Carousel",
    "About Us",
    "Contact Us",
  ].map((name) => normalizeTabLabel(name)),
);

export const isMandatoryMissingTabName = (name) => {
  const keys = collectMatchKeys(name);
  return [...keys].some((key) => MANDATORY_MISSING_TAB_LABELS.has(key));
};

export function cachePlanScopedTabs(micrositeId, tabs) {
  if (!micrositeId || !Array.isArray(tabs)) return;
  planScopedTabsByMicrositeId.set(String(micrositeId), tabs);
}

export function invalidateMicrositeTabsCache(micrositeId) {
  if (!micrositeId) return;
  const cacheKey = String(micrositeId);
  planScopedTabsByMicrositeId.delete(cacheKey);
  allTabsByMicrositeId.delete(cacheKey);
}

export const MICROSITE_PRO_SETTINGS_UPDATED_EVENT =
  "microsite-pro-settings-updated";

export const tabMatchesMissingName = (tab, missingName) => {
  const missingKeys = collectMatchKeys(missingName);
  const tabKeys = collectMatchKeys(tab?.displayName ?? "");

  for (const missingKey of missingKeys) {
    if (tabKeys.has(missingKey)) return true;
  }
  return false;
};

export async function fetchPlanScopedTabs(
  micrositeId,
  { force = false, includeAllTabs = false } = {},
) {
  if (!micrositeId) return [];

  const cacheKey = String(micrositeId);
  const cache = includeAllTabs ? allTabsByMicrositeId : planScopedTabsByMicrositeId;

  if (!force && cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  const [tabsResponse, plansResponse] = await Promise.all([
    getRequest(userServiceApi.getMicroSiteTabConfig(micrositeId)),
    getRequest(userServiceApi.handleFetchPlans(micrositeId)).catch(() => null),
  ]);

  const allTabs = Array.isArray(tabsResponse?.data) ? tabsResponse.data : [];
  allTabsByMicrositeId.set(cacheKey, allTabs);

  const planScopedTabs = filterTabsBySubscriptionPlan(
    allTabs,
    plansResponse?.data?.planDetails,
  );
  cachePlanScopedTabs(micrositeId, planScopedTabs);

  const result = includeAllTabs ? allTabs : planScopedTabs;
  cache.set(cacheKey, result);
  return result;
}

/** Payload items to turn secondary toggle off for tabs listed as missing. */
export function buildMissingTabsToggleOffPayload(planScopedTabs, missingTabNames) {
  if (!Array.isArray(planScopedTabs) || !Array.isArray(missingTabNames)) {
    return [];
  }

  const payload = [];

  missingTabNames.forEach((missingName) => {
    if (isMandatoryMissingTabName(missingName)) return;

    const tab = planScopedTabs.find((t) => tabMatchesMissingName(t, missingName));
    if (!tab?.id) return;
    if (isMandatoryMissingTabName(tab.displayName)) return;

    const alreadyInPayload = payload.some((item) => item.id === tab.id);
    if (alreadyInPayload) return;

    payload.push({
      id: tab.id,
      secondaryToggle: false,
    });
  });

  return payload;
}

export function applySecondaryToggleOffInCache(micrositeId, payload) {
  if (!micrositeId || !Array.isArray(payload) || !payload.length) return;

  const cacheKey = String(micrositeId);
  const idsToDisable = new Set(payload.map((item) => item.id));

  const patchList = (tabs) => {
    if (!Array.isArray(tabs)) return;
    const updated = tabs.map((tab) =>
      idsToDisable.has(tab.id) ? { ...tab, secondaryToggle: false } : tab,
    );
    return updated;
  };

  if (planScopedTabsByMicrositeId.has(cacheKey)) {
    planScopedTabsByMicrositeId.set(
      cacheKey,
      patchList(planScopedTabsByMicrositeId.get(cacheKey)),
    );
  }
  if (allTabsByMicrositeId.has(cacheKey)) {
    allTabsByMicrositeId.set(
      cacheKey,
      patchList(allTabsByMicrositeId.get(cacheKey)),
    );
  }
}
